import React, { useEffect, useMemo } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { CenteredLoadingSpinner } from '@ole-ui/ole-ui-components';

import GenericViewError from 'components/Error/GenericViewError';
import { handleError } from 'services/ErrorService';
import PremiumSessionsStore from 'stores/PremiumSessions';
import useMobxStores from 'hooks/useMobxStores';
import PremiumSessionDashboardStore from './store';
import './styles.scss';

const SessionDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'class-dashboard' */ 'components/Classes/Dashboard'
    ) as Promise<{
      default: React.ComponentType<{
        data: object;
      }>;
    }>,
);

type SessionRouteParams = {
  sessionid?: string;
  sessionslug?: string;
};

const PremiumSessionDashboardView = () => {
  const { routerStore, userStore, premiumSessionsStore } = useMobxStores();
  const params = ((routerStore.route as { params?: SessionRouteParams })
    ?.params ?? {}) as SessionRouteParams;
  const sessionslug = params.sessionslug ?? '';
  const { sessionid } = params;

  const store = useMemo(() => new PremiumSessionDashboardStore(), [sessionid]);

  const pageTitle = useMemo(() => {
    const splitSessionSlug = sessionslug.split('-');
    return splitSessionSlug.length >= 2
      ? `${splitSessionSlug[0]} - ${splitSessionSlug[1]} - Session ${splitSessionSlug[2]}`
      : '';
  }, [sessionslug]);

  useEffect(() => {
    if (pageTitle) {
      document.title = pageTitle;
    }
  }, [pageTitle]);

  useEffect(() => {
    const loadPremiumSessions = () =>
      new Promise<void>((resolve, reject) => {
        if (!premiumSessionsStore.loaded) {
          premiumSessionsStore
            .getPremiumSessions()
            .then(() => resolve())
            .catch((error) => reject(error));
        } else {
          resolve();
        }
      });

    const setDataForPremiumSessions = (
      sessionData: Record<string, unknown> | null = null,
    ) => {
      const premVTSession =
        sessionData ||
        premiumSessionsStore.getScheduledSessionBySessionId(sessionid);

      store.data = (premVTSession as Record<string, unknown>) || {};
      store.data.course =
        PremiumSessionsStore.getCourseMetaFromSessionSlug(sessionslug);
      store.userSubscription = userStore.subscription;
    };

    if (userStore.isPremiumSubscriber) {
      loadPremiumSessions().then(() => {
        setDataForPremiumSessions();
      });
      return;
    }

    const premiumSessionId = sessionid ?? '';
    premiumSessionsStore
      .fetchPremiumSessionBySessionId(premiumSessionId)
      .then((session) => {
        setDataForPremiumSessions(session);
      })
      .catch(() => {
        setDataForPremiumSessions({});
      });
  }, [premiumSessionsStore, sessionid, sessionslug, store, userStore]);

  return (
    <div className="premium-session-dashboard-view">
      <React.Suspense fallback={<CenteredLoadingSpinner />}>
        {store.data && <SessionDashboard data={toJS(store.data)} />}
      </React.Suspense>
    </div>
  );
};

export default withErrorBoundary(
  observer(PremiumSessionDashboardView),
  GenericViewError,
  handleError,
);
